/**
 * Leitura de pinout: a busca paginada e os filtros.
 *
 * O endpoint aceita doze parâmetros e todos entram aqui desde já, mesmo que a
 * barra de filtros da semana 2 só use alguns — assim a semana 2 acrescenta
 * interface, não encanamento.
 *
 * Os filtros por id vão como **lista separada por vírgula**, que é o formato do
 * legado e o que a API espera (`componentes=10,11`). Os de texto vão repetidos
 * (`pinosExatos=A01&pinosExatos=A02`): o valor escolhido pode ter vírgula.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { api, obter } from "./cliente"
import { chaves, POR_PAGINA } from "./query"
import type { PaginaResponse, PinoutResponse } from "./tipos"
import type { Catalogo, ItemDeCatalogo } from "./catalogos"

export type OrdemPinout = "recentes" | "pino" | "componente"

export type FiltroPinout = {
  /** ids, separados por vírgula quando forem vários */
  componentes?: string
  conectores?: string
  circuitos?: string
  cores?: string
  chicotes?: string
  restricoes?: string
  /** RN-01: filtrar por modelo aplica a visibilidade por restrição — não é só um filtro. */
  modelos?: string
  /**
   * Os valores escolhidos nos seletores: casam inteiros. Vão repetidos na query
   * (`pinosExatos=A01&pinosExatos=A02`), não por vírgula — observação tem vírgula.
   * "" é a opção "(vazio)".
   */
  pinos?: string[]
  obs?: string[]
  arquivosOriginais?: string[]
  ordem?: OrdemPinout
}

/** O filtro nos nomes que a API espera — a grade, a exportação e as opções usam os mesmos. */
function paramsDoFiltro(filtro: FiltroPinout) {
  const lista = (v?: string[]) => (v && v.length ? v : undefined)
  return {
    componentes: filtro.componentes || undefined,
    conectores: filtro.conectores || undefined,
    circuitos: filtro.circuitos || undefined,
    cores: filtro.cores || undefined,
    chicotes: filtro.chicotes || undefined,
    restricoes: filtro.restricoes || undefined,
    modelos: filtro.modelos || undefined,
    pinosExatos: lista(filtro.pinos),
    obsExatas: lista(filtro.obs),
    fontesExatas: lista(filtro.arquivosOriginais),
  }
}

/** A API entende "pino" e "componente"; qualquer outra coisa cai no padrão do legado, pinout_id DESC. */
function ordemDaApi(o?: OrdemPinout): string | undefined {
  return o === "pino" || o === "componente" ? o : undefined
}

export function usarPinouts(projetoId: number, filtro: FiltroPinout, pagina: number) {
  return useQuery({
    queryKey: chaves.pinouts(projetoId, filtro, pagina),
    enabled: Number.isFinite(projetoId) && projetoId > 0,
    // Trocar de página mantém a página anterior na tela enquanto a nova vem:
    // a grade não pisca nem perde a altura, e a pessoa não perde o lugar.
    placeholderData: keepPreviousData,
    queryFn: () =>
      obter<PaginaResponse<PinoutResponse>>("api/pinouts", {
        projetoId, ...paramsDoFiltro(filtro), ordem: ordemDaApi(filtro.ordem), pagina,
      }),
  })
}

// ------------------------------------------------------------ opções dos filtros

/** Os campos da grade que viram seletor. */
export type CampoDoFiltro =
  | "componente" | "conector" | "circuito" | "cor" | "chicote" | "restricao" | "modelo"
  | "pino" | "obs" | "fonte"

/** Qual chave do filtro cada campo ocupa — é a que sai do filtro quando o seletor dele pede opções. */
const CHAVE_DO_CAMPO: Record<CampoDoFiltro, keyof FiltroPinout> = {
  componente: "componentes", conector: "conectores", circuito: "circuitos", cor: "cores",
  chicote: "chicotes", restricao: "restricoes", modelo: "modelos",
  pino: "pinos", obs: "obs", fonte: "arquivosOriginais",
}

type OpcoesResponse = {
  itens: { valor: string; rotulo: string; codigo?: string | null; usos: number }[]
  total: number
}

const opcaoDoCatalogo = (o: OpcoesResponse["itens"][number]): ItemDeCatalogo<string> =>
  ({ id: o.valor, rotulo: o.rotulo, codigo: o.codigo || undefined, usos: o.usos })

/**
 * As opções de um seletor da grade: o que o campo tem **nos pinouts que os
 * outros filtros deixam passar**. É o funil — escolhido um componente, o
 * seletor de pino só lista os pinos dele.
 *
 * Só consulta com `ligado` (o seletor aberto): fechado, ninguém está olhando a
 * lista. A chave fica sem o próprio campo, então marcar mais um pino não
 * refaz a lista de pinos; e mora sob `["pinouts", projetoId]`, que é o que o
 * lançamento invalida ao gravar.
 *
 * Os valores vêm como texto; nos campos de cadastro é o id, e quem guarda no
 * filtro converte.
 */
export function usarOpcoesDoPinout(
  projetoId: number, campo: CampoDoFiltro, filtro: FiltroPinout, ligado: boolean,
): Catalogo<string> {
  const outros = { ...filtro, [CHAVE_DO_CAMPO[campo]]: undefined, ordem: undefined }
  const chave = ["pinouts", projetoId, "opcoes", campo, outros] as const
  const consultar = (busca?: string) =>
    obter<OpcoesResponse>("api/pinouts/opcoes", { projetoId, campo, busca, ...paramsDoFiltro(outros) })

  const { data, isPending, isError } = useQuery({
    queryKey: chave,
    enabled: ligado && projetoId > 0,
    staleTime: 60_000,
    queryFn: () => consultar(),
  })

  const itens = (data?.itens ?? []).map(opcaoDoCatalogo)
  const total = data?.total ?? 0
  return {
    itens,
    total,
    completo: total <= itens.length,
    carregando: ligado && isPending,
    erro: ligado && isError,
    buscar: async (termo) => (await consultar(termo)).itens.map(opcaoDoCatalogo),
    chave,
  }
}

export function totalDePaginas(total: number): number {
  return Math.max(1, Math.ceil(total / POR_PAGINA))
}


// ------------------------------------------------------------ exportar

/**
 * Baixa a planilha dos pinouts **com os filtros que estão na tela**.
 *
 * É o `Exportar` do legado, que servia um HTML com extensão .xls; aqui sai xlsx
 * de verdade, ou csv. Levar os mesmos filtros importa: exportar o projeto
 * inteiro quando se está olhando um componente é dar trabalho de recortar
 * depois — e são 200 linhas por bloco, como no original.
 */
export async function exportarPinouts(
  projetoId: number, filtro: FiltroPinout, formato: "xlsx" | "csv", pagina = 1,
): Promise<{ nome: string; bytes: number }> {
  const r = await api.get<Blob>("api/pinouts/exportar", {
    params: { projetoId, ...paramsDoFiltro(filtro), ordem: ordemDaApi(filtro.ordem), pagina, formato },
    responseType: "blob",
  })

  // O nome vem no Content-Disposition; sem ele o navegador salva "exportar".
  const disp = String(r.headers?.["content-disposition"] ?? "")
  const achado = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp)
  const nome = achado ? decodeURIComponent(achado[1]!) : `pinouts-${projetoId}.${formato}`

  const url = URL.createObjectURL(r.data)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)

  return { nome, bytes: r.data.size }
}

// --------------------------------------------------------- conferencia

/**
 * Pinouts que já batem com este conjunto — o aviso de duplicata **antes** de
 * gravar.
 *
 * A inclusão em lote já recusa duplicata (RN-04) e devolve "Pinout já
 * cadastrado". Isto é o mesmo aviso, só que enquanto a pessoa ainda está
 * digitando: descobrir no Enter é melhor que descobrir depois de montar as duas
 * ligações e apertar Ctrl+Enter.
 */
export async function conferirDuplicata(
  projetoId: number, componenteId: number, conectorId: number,
  circuitoId: number, pino: string, pinoutId = 0,
): Promise<PinoutResponse[]> {
  return await obter<PinoutResponse[]>("api/pinouts/conferencia", {
    projetoId, componenteId, conectorId, circuitoId, pino, pinoutId,
  })
}
